import { useEffect, useState } from 'react';
import { useBusiness } from '../context/BusinessContext';
import { listLeads, updateLead } from '../services/api';
import { Lead, LeadStatus } from '../types';
import { format } from 'date-fns';

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
];

export default function LeadsPage() {
  const { currentBusiness } = useBusiness();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');

  useEffect(() => {
    const fetchLeads = async () => {
      if (!currentBusiness) return;

      setLoading(true);
      try {
        const data = await listLeads(currentBusiness.id, filterStatus || undefined);
        setLeads(data);
      } catch (error) {
        console.error('Error fetching leads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [currentBusiness, filterStatus]);

  const handleStatusChange = async (leadId: string, newStatus: LeadStatus) => {
    if (!currentBusiness) return;

    try {
      await updateLead(currentBusiness.id, leadId, { status: newStatus });
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        )
      );
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  if (!currentBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a business</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-header mb-0">Leads</h1>
        <div className="flex items-center gap-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input w-auto"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : leads.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No leads found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Contact</th>
                  <th className="table-header">Reason</th>
                  <th className="table-header">Source</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <div>
                        <p className="font-medium text-gray-900">
                          {lead.callerName || 'Unknown'}
                        </p>
                        <p className="text-gray-500 text-xs">{lead.callerPhone}</p>
                        {lead.callerEmail && (
                          <p className="text-gray-500 text-xs">{lead.callerEmail}</p>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <p className="text-sm text-gray-900 max-w-xs truncate">
                        {lead.reason || '-'}
                      </p>
                    </td>
                    <td className="table-cell">
                      <span className="text-xs text-gray-500">Phone Call</span>
                    </td>
                    <td className="table-cell">
                      {lead.createdAt?.toDate()
                        ? format(lead.createdAt.toDate(), 'MMM d, yyyy')
                        : 'Unknown'}
                    </td>
                    <td className="table-cell">
                      <select
                        value={lead.status}
                        onChange={(e) =>
                          handleStatusChange(lead.id, e.target.value as LeadStatus)
                        }
                        className={`text-xs font-medium rounded-full px-3 py-1 border-0 focus:ring-2 focus:ring-primary-500 ${
                          lead.status === 'new'
                            ? 'bg-blue-100 text-blue-800'
                            : lead.status === 'contacted'
                            ? 'bg-yellow-100 text-yellow-800'
                            : lead.status === 'qualified'
                            ? 'bg-purple-100 text-purple-800'
                            : lead.status === 'converted'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="table-cell">
                      <p className="text-sm text-gray-500 max-w-xs truncate">
                        {lead.notes || '-'}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
